import React, { ReactElement } from 'react';
import { useIntl } from 'react-intl';
import IntelligencePageLayout from '@fuww/library/src/Layouts/IntelligencePage';
import AdaptiveCard from '@fuww/library/src/Cards/AdaptiveCard';
import breakpoints from '@fuww/library/src/Theme/breakpoints';
import Layout from '../components/Layout';
import Hero from '../components/Hero';
import Head from '../components/Head';
import messages from '../lib/messages.json';
import LinkAndAnchor from '../components/LinkAndAnchor';
import ImageWithUrls from '../components/ImageWithUrls';
import getProxiedImages from '../lib/getProxiedImages';
import { companyProfilesCardImageConfigurations }
  from '../lib/imageConfigurations';
import intelligencePageCardsData from '../lib/intelligencePageCardsData';
import redirectRouteToDomain from '../lib/redirectRouteToDomain';

const headerImageUrls = {
  mobile: [
    'https://r.fashionunited.com/R7armzT2ipxCu8BdRmz5_8pGyF6TeiPuYc35XcJgO9g/resize:fill:42:34:0/gravity:ce/quality:10/aHR0cHM6Ly9tZWRpYS5mYXNoaW9udW5pdGVkLmNvbS9tZWRpYS9pbWFnZXMvZmFzaGlvbi1idXNpbmVzcy1kYXRhLW1vYmlsZS5qcGc',
    'https://r.fashionunited.com/qmsx0YgnJ2bIYwruGDkrHwj91cE_4G7EYPx1rZzAKcI/resize:fill:360:288:0/gravity:ce/quality:70/aHR0cHM6Ly9tZWRpYS5mYXNoaW9udW5pdGVkLmNvbS9tZWRpYS9pbWFnZXMvZmFzaGlvbi1idXNpbmVzcy1kYXRhLW1vYmlsZS5qcGc',
    'https://r.fashionunited.com/nAxvUdDn1jQH9tkAKtLxUSo96zO7Ua6_S562L9eAfHM/resize:fill:720:576:0/gravity:ce/quality:70/aHR0cHM6Ly9tZWRpYS5mYXNoaW9udW5pdGVkLmNvbS9tZWRpYS9pbWFnZXMvZmFzaGlvbi1idXNpbmVzcy1kYXRhLW1vYmlsZS5qcGc',
  ],
  desktop: [
    'https://r.fashionunited.com/YdeTsbr2t11e0trzLhUtnQIvqoJ0Fb8I-BwZZ_GY7GE/resize:fill:42:8:0/gravity:ce/quality:10/aHR0cHM6Ly9tZWRpYS5mYXNoaW9udW5pdGVkLmNvbS9tZWRpYS9pbWFnZXMvZmFzaGlvbi1idXNpbmVzcy1kYXRhLWRlc2t0b3AuanBn',
    'https://r.fashionunited.com/_mRITBtrdfkDo9qfOxvSPqQzyl0ieQNSMktMlh9jKbM/resize:fill:1350:277:0/gravity:ce/quality:70/aHR0cHM6Ly9tZWRpYS5mYXNoaW9udW5pdGVkLmNvbS9tZWRpYS9pbWFnZXMvZmFzaGlvbi1idXNpbmVzcy1kYXRhLWRlc2t0b3AuanBn',
    'https://r.fashionunited.com/HN5bHkJkAPO7oZVZuR-amsrhpgB0I4XTplugf2RG40U/resize:fill:2560:526:0/gravity:ce/quality:70/aHR0cHM6Ly9tZWRpYS5mYXNoaW9udW5pdGVkLmNvbS9tZWRpYS9pbWFnZXMvZmFzaGlvbi1idXNpbmVzcy1kYXRhLWRlc2t0b3AuanBn',
    'https://r.fashionunited.com/ambF-i28XPZpiZFycKsUZuOtXJGR6n3wwwtTY4Gz_Cc/resize:fill:5120:1052:0/gravity:ce/quality:70/aHR0cHM6Ly9tZWRpYS5mYXNoaW9udW5pdGVkLmNvbS9tZWRpYS9pbWFnZXMvZmFzaGlvbi1idXNpbmVzcy1kYXRhLWRlc2t0b3AuanBn',
  ],
};

const IntelligencePage = () => {
  const intl = useIntl();

  return (
    <>
      <Head
        title={intl.formatMessage(messages['intelligence.header'])}
        description={intl.formatMessage(messages['intelligence.description'])}
      />
      <IntelligencePageLayout
        description={(
          <div
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={{
              __html: intl.formatMessage(messages['intelligence.introText']),
            }}
          />
        )}
        pageCards={intelligencePageCardsData.map(({
          title,
          description,
          href,
          imageUrls: originalImageUrls,
        }) => {
          const { imageUrls, srcSet } = getProxiedImages(
            { imageConfigurations: companyProfilesCardImageConfigurations },
            originalImageUrls,
          );

          return (
            <LinkAndAnchor key={title} href={href} display="flex">
              <AdaptiveCard
                title={title}
                textAlign="left"
                description={description}
                image={imageUrls[0] && (
                  <ImageWithUrls
                    sizes={`
                      (min-width: 1440px) 318px,
                      (min-width: ${breakpoints.lg}px) calc(25vw - 42px),
                      (min-width: ${breakpoints.md}px) calc(50vw - 52px),
                      calc(100vw - 72px)
                    `}
                    srcSet={srcSet}
                    imageUrls={imageUrls}
                    alt={title}
                    disableRoundedCorners
                  />
                )}
              />
            </LinkAndAnchor>
          );
        })}
      />
    </>
  );
};

IntelligencePage.getLayout = (page: ReactElement) => (
  <Layout
    header={(
      <Hero
        alt="Banner image"
        imageUrls={headerImageUrls}
      />
    )}
  >
    {page}
  </Layout>
);

IntelligencePage.getInitialProps = redirectRouteToDomain('intelligence');

export default IntelligencePage;
